import { useCallback, useEffect, useRef, useState } from "react";
import { api, onSessionExpired } from "./api";
import type { Catalog, Introduction, Need, NeedDraft, Session } from "./api";
import {
  Badge,
  Empty,
  Icon,
  PageTitle,
  dateTime,
  errorMessage,
} from "./components";
import type { IconName } from "./components";
import { AuthDialog, ProfileScreen } from "./Identity";
import {
  AgendaScreen,
  CardScreen,
  PlacesScreen,
  ReserveScreen,
  emptyReservation,
} from "./Discovery";
import { GuildScreen, RequestList, type GuildTab } from "./Guild";
import { CrmScreen } from "./Crm";
import {
  OperationalAgenda,
  OperationalReserve,
  Engagements,
} from "./OperationalDiscovery";
import { pilotApi } from "./place-pilot";
import type { PilotCatalog, PilotEngagements } from "./place-pilot-types";
import { parseAuthCallback } from "./auth-callback";

type Route =
  | "lobby"
  | "agenda"
  | "places"
  | "guild"
  | "card"
  | "reserve"
  | "profile"
  | "staff"
  | "crm";
const routes: Array<{ id: Route; label: string; icon: IconName }> = [
  { id: "lobby", label: "Lobby", icon: "lobby" },
  { id: "agenda", label: "Agenda", icon: "agenda" },
  { id: "places", label: "Lieux", icon: "places" },
  { id: "guild", label: "Guilde", icon: "guild" },
];
const initialSession: Session = {
  person: null,
  role: null,
  profile: null,
  csrf_token: null,
};
const emptyCatalog: Catalog = {
  places: [],
  events: [],
  spaces: [],
  demo: false,
};
const initialDraft: NeedDraft = {
  help_terms: [],
  context: "",
  distance_preference: "any",
  place_id: null,
  require_available: false,
  visibility_scope: "members",
};

const draftStorageKey = "avalon.place-pilot.draft.v1";
type SavedJourney = {
  version?: number;
  route?: Route;
  eventId?: string;
  person_id?: string;
  reservation?: typeof emptyReservation;
};
function savedJourney(): SavedJourney {
  try {
    const value: unknown = JSON.parse(
      localStorage.getItem(draftStorageKey) || "{}",
    );
    if (
      !value ||
      typeof value !== "object" ||
      !("version" in value) ||
      value.version !== 1
    )
      return {};
    const stored = value as Record<string, unknown>;
    const reservation = { ...emptyReservation };
    if (stored.reservation && typeof stored.reservation === "object") {
      const source = stored.reservation as Record<string, unknown>;
      for (const field of [
        "space_id",
        "date",
        "time",
        "duration",
        "people",
        "purpose",
        "request_key",
        "request_fingerprint",
      ] as const) {
        if (typeof source[field] === "string" && source[field].length <= 5000)
          reservation[field] = source[field];
      }
    }
    return {
      version: 1,
      route:
        stored.route === "agenda" && typeof stored.eventId === "string"
          ? "agenda"
          : stored.route === "reserve" &&
              !!(
                reservation.space_id ||
                reservation.date ||
                reservation.purpose
              )
            ? "reserve"
            : "lobby",
      eventId:
        typeof stored.eventId === "string" && stored.eventId.length <= 100
          ? stored.eventId
          : undefined,
      person_id:
        typeof stored.person_id === "string" &&
        /^[a-f0-9-]{36}$/.test(stored.person_id)
          ? stored.person_id
          : undefined,
      reservation,
    };
  } catch {
    return {};
  }
}
const recoveredJourney = savedJourney();
export default function App() {
  const [route, setRoute] = useState<Route>(
    location.pathname === "/intendance"
      ? "staff"
      : location.pathname === "/crm"
        ? "crm"
        : ["agenda", "reserve"].includes(recoveredJourney.route || "")
          ? (recoveredJourney.route as Route)
          : "lobby",
  );
  const [session, setSession] = useState<Session>(initialSession);
  const [guildTab, setGuildTab] = useState<GuildTab>("discover");
  const currentPerson = useRef(session.person?.id);
  currentPerson.current = session.person?.id;
  const [catalog, setCatalog] = useState<Catalog>(emptyCatalog);
  const [needs, setNeeds] = useState<Need[]>([]);
  const [requests, setRequests] = useState<Introduction[]>([]);
  const [availability, setAvailability] = useState<string | null>(null);
  const [draft, setDraft] = useState<NeedDraft>(initialDraft);
  const [placeId, setPlaceId] = useState("");
  const [selectedPlace, setSelectedPlace] = useState<string | null>(null);
  const [reservationDraft, setReservationDraft] = useState(() => ({
    ...emptyReservation,
    ...(recoveredJourney.reservation || {}),
  }));
  const [eventId, setEventId] = useState<string | null>(
    recoveredJourney.eventId || null,
  );
  const [reservationId, setReservationId] = useState<string | null>(null);
  const [pilotCatalog, setPilotCatalog] = useState<PilotCatalog | null>(null);
  const [engagements, setEngagements] = useState<PilotEngagements>({
    registrations: [],
    reservations: [],
  });
  const draftOwner = useRef<string | null>(recoveredJourney.person_id || null);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"signup" | "login">("signup");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const startup = useRef<Promise<{
    session: Session;
    catalog: Catalog;
    callbackError: string;
    confirmed: boolean;
  }> | null>(null);
  const [privateStateVersion, setPrivateStateVersion] = useState(0);
  const clearPrivateState = useCallback(() => {
    setNeeds([]);
    setRequests([]);
    setAvailability(null);
    setDraft(initialDraft);
    setReservationDraft(emptyReservation);
    setEventId(null);
    setReservationId(null);
    setEngagements({ registrations: [], reservations: [] });
    try {
      localStorage.removeItem(draftStorageKey);
    } catch {}
    setGuildTab("discover");
    setPrivateStateVersion((version) => version + 1);
  }, []);
  const applySession = useCallback(
    (next: Session) => {
      // Preserve a visitor's draft on first sign-in, never a previous person's data.
      if (
        (currentPerson.current || draftOwner.current) &&
        (currentPerson.current || draftOwner.current) !== next.person?.id
      )
        clearPrivateState();
      draftOwner.current = next.person?.id || null;
      currentPerson.current = next.person?.id;
      setSession(next);
    },
    [clearPrivateState],
  );
  useEffect(
    () =>
      onSessionExpired(() => {
        if (!currentPerson.current) return;
        applySession(initialSession);
        setAuthOpen(false);
        setNotice("");
        setError("Votre session a expiré. Connectez-vous pour poursuivre.");
      }),
    [applySession],
  );
  useEffect(() => {
    try {
      localStorage.setItem(
        draftStorageKey,
        JSON.stringify({
          version: 1,
          route,
          eventId,
          reservation: reservationDraft,
          person_id: draftOwner.current,
        }),
      );
    } catch {}
  }, [route, eventId, reservationDraft, session.person?.id]);
  const refresh = useCallback(async () => {
    if (!session.person) return;
    const [
      needsResult,
      requestsResult,
      availabilityResult,
      pilotResult,
      catalogResult,
    ] = await Promise.all([
      api.needs(),
      api.introductions(),
      api.availability(),
      pilotCatalog ? pilotApi.engagements() : Promise.resolve(null),
      pilotCatalog ? pilotApi.catalog() : Promise.resolve(null),
    ]);
    if (currentPerson.current === session.person.id) {
      if (pilotResult) setEngagements(pilotResult);
      if (catalogResult) {
        setPilotCatalog(catalogResult);
        setCatalog(catalogResult);
      }
      setNeeds(needsResult.needs);
      setRequests(requestsResult.requests);
      setAvailability(availabilityResult.availability.until);
    }
  }, [session.person?.id, !!pilotCatalog]);
  useEffect(() => {
    let active = true;
    if (!startup.current)
      startup.current = (async () => {
        const cleanCallback =
          location.pathname === "/auth/callback" &&
          !location.search &&
          !location.hash;
        const callback = parseAuthCallback(location.href, {
          platform: "web",
          origin: location.origin,
        });
        // Remove credentials before any request, render or retry. No URL is logged.
        if (location.pathname === "/auth/callback")
          history.replaceState(null, "", "/auth/callback");
        let callbackError = "",
          confirmed = false,
          currentSession: Session;
        try {
          if (callback.kind === "credentials") {
            currentSession = await api.exchange(callback.credentials);
            if (!currentSession.person)
              throw new Error(
                "La confirmation n’a pas créé de session. Connectez-vous pour poursuivre.",
              );
            confirmed = true;
          } else {
            if (callback.kind === "invalid")
              callbackError =
                "Ce lien de confirmation est incomplet ou a expiré. Reprenez la connexion ou saisissez le code reçu par e-mail.";
            currentSession = await api.session();
          }
        } catch {
          callbackError =
            "La confirmation n’a pas abouti. Reprenez la connexion ou saisissez le code reçu par e-mail.";
          currentSession = await api.session();
        }
        if (cleanCallback && currentSession.person) callbackError = "";
        return {
          session: currentSession,
          catalog: await api.catalog(),
          callbackError,
          confirmed,
        };
      })();
    startup.current
      .then((result) => {
        if (active) {
          applySession(result.session);
          const loaded = result.catalog as PilotCatalog;
          // Practical fields remain in the owner details record; this is a view projection.
          loaded.places = loaded.places.map((p) => ({ ...p, ...p.details }));
          setCatalog(loaded);
          if (loaded.policy) setPilotCatalog(loaded);
          if (result.callbackError) setError(result.callbackError);
          if (result.confirmed)
            setNotice("Votre adresse est confirmée. Bienvenue dans Avalon.");
        }
      })
      .catch((error) => {
        if (active) setError(errorMessage(error));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    if (session.person)
      void refresh().catch((error) => setError(errorMessage(error)));
    else {
      setNeeds([]);
      setRequests([]);
      setAvailability(null);
    }
  }, [refresh, session.person?.id]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    function onFocus() {
      if (session.person)
        void refresh().catch((error) => setError(errorMessage(error)));
    }
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh, session.person?.id]);
  useEffect(() => {
    document.title = `Avalon · ${route === "staff" ? "Intendance" : route === "crm" ? "CRM" : [...routes, { id: "card", label: "Ma carte" }, { id: "reserve", label: "Réserver" }, { id: "profile", label: "Profil" }].find((item) => item.id === route)?.label}`;
  }, [route]);

  function navigate(next: Route, guildDestination?: GuildTab) {
    if (next === "places" && route !== "places") setSelectedPlace(null);
    if (next === "guild" && (guildDestination || route !== "guild")) {
      setGuildTab(guildDestination ?? "discover");
    }
    if (
      next === "guild" &&
      guildDestination === "requests" &&
      currentPerson.current
    )
      void refresh().catch((error) => setError(errorMessage(error)));
    setRoute(next);
    setError("");
    setNotice("");
    window.scrollTo({ top: 0, behavior: "instant" });
  }
  async function mutate(action: () => Promise<unknown>, message?: string) {
    setBusy(true);
    setError("");
    try {
      await action();
      await refresh();
      if (message) setNotice(message);
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }
  const respond = (id: string, action: "accept" | "decline" | "withdraw") =>
    void mutate(
      () => api.respond(id, action),
      action === "accept"
        ? "Votre accord est enregistré."
        : action === "decline"
          ? "La demande a été déclinée."
          : "Votre retrait est enregistré.",
    );
  async function logout() {
    setBusy(true);
    setError("");
    try {
      applySession(await api.logout());
      setNotice("Vous êtes déconnecté.");
      setRoute("lobby");
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }
  const activeAvailability =
    availability && new Date(availability).valueOf() > now
      ? availability
      : null;
  const incoming = requests.filter(
    (request) =>
      request.recipient_person_id === session.person?.id &&
      request.status === "pending" &&
      new Date(request.expires_at).valueOf() > now,
  );
  const displayRequests = requests.map((request) =>
    request.status === "pending" &&
    new Date(request.expires_at).valueOf() <= now
      ? { ...request, status: "expired" as const }
      : request,
  );

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main">
        Aller au contenu
      </a>
      <header className="app-header">
        <button
          className="wordmark"
          onClick={() => navigate("lobby")}
          aria-label="Avalon, revenir au Lobby"
        >
          Avalon
          <span className="brand-dot" />
        </button>
        <div className="shortcuts">
          {(
            [
              { id: "card", label: "Ma carte", icon: "card" },
              { id: "reserve", label: "Réserver", icon: "plus" },
              { id: "profile", label: "Profil", icon: "profile" },
            ] as const
          ).map((item) => (
            <button
              key={item.id}
              aria-pressed={route === item.id}
              onClick={() => navigate(item.id)}
            >
              <Icon name={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      </header>
      <div className="app-layout">
        <nav className="primary-nav" aria-label="Navigation principale">
          {routes.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.id)}
              aria-current={route === item.id ? "page" : undefined}
            >
              <Icon name={item.icon} />
              <span>{item.label}</span>
              {item.id === "guild" && incoming.length > 0 && (
                <span
                  className="nav-dot"
                  aria-label={`${incoming.length} demande à traiter`}
                />
              )}
            </button>
          ))}
          {session.role === "staff" && (
            <button
              className="staff-nav"
              onClick={() => navigate("staff")}
              aria-current={route === "staff" ? "page" : undefined}
            >
              <Icon name="shield" />
              <span>Intendance</span>
            </button>
          )}
          {session.role === "crm" && (
            <button
              className="staff-nav"
              onClick={() => navigate("crm")}
              aria-current={route === "crm" ? "page" : undefined}
            >
              <Icon name="shield" />
              <span>CRM privé</span>
            </button>
          )}
        </nav>
        <main id="main" tabIndex={-1}>
          <div className="environment-line">
            <span className="environment-dot" />
            Environnement de développement
            <span>
              {session.person ? "Compte connecté" : "Exploration libre"}
            </span>
          </div>
          {error && (
            <div role="alert" className="notice error">
              <span>{error}</span>
              <button
                className="icon-button"
                onClick={() => setError("")}
                aria-label="Fermer le message"
              >
                <Icon name="close" />
              </button>
            </div>
          )}
          {notice && (
            <div role="status" className="notice">
              <span>{notice}</span>
              <button
                className="icon-button"
                onClick={() => setNotice("")}
                aria-label="Fermer le message"
              >
                <Icon name="close" />
              </button>
            </div>
          )}
          {loading ? (
            <Empty icon="clock" title="Bienvenue à Avalon…">
              Nous préparons votre Lobby.
            </Empty>
          ) : (
            <>
              {route === "lobby" && (
                <>
                  <PageTitle
                    eyebrow="Votre point de départ"
                    title={
                      session.person
                        ? `Bonjour ${session.person.display_name.split(" ")[0]}.`
                        : "Bienvenue chez vous."
                    }
                  >
                    {session.person
                      ? "Un peu de place pour ce qui compte aujourd’hui."
                      : "Des lieux, des rencontres et des idées à faire grandir ensemble."}
                  </PageTitle>
                  {!session.person ? (
                    <section className="welcome-card">
                      <span className="eyebrow">Les liens commencent ici</span>
                      <h2>
                        Quelque chose à partager.
                        <br />
                        Quelqu’un à rencontrer.
                      </h2>
                      <p>
                        Exprimez un besoin, faites découvrir votre savoir-faire
                        et trouvez des personnes avec qui avancer.
                      </p>
                      <button
                        className="button charcoal"
                        onClick={() => {
                          setAuthMode("signup");
                          setAuthOpen(true);
                        }}
                      >
                        Créer mon compte gratuit
                        <Icon name="arrow" />
                      </button>
                      <button
                        className="text-button"
                        onClick={() => {
                          setAuthMode("login");
                          setAuthOpen(true);
                        }}
                      >
                        J’ai déjà un compte
                      </button>
                      <div className="hero-orbit" aria-hidden="true" />
                    </section>
                  ) : (
                    <>
                      <section>
                        <div className="section-heading">
                          <h2>À votre attention</h2>
                          <Badge>{incoming.length} à traiter</Badge>
                        </div>
                        {incoming.length ? (
                          <RequestList
                            requests={incoming}
                            personId={session.person.id}
                            onRespond={respond}
                            busy={busy}
                          />
                        ) : (
                          <div className="panel quiet-panel">
                            <span className="icon-circle">
                              <Icon name="check" />
                            </span>
                            <div>
                              <h3>Tout est à jour.</h3>
                              <p className="muted">
                                Vous n’avez aucune demande en attente de
                                réponse.
                              </p>
                            </div>
                          </div>
                        )}
                      </section>
                      <section className="panel">
                        <div className="section-heading">
                          <h2>Mes engagements</h2>
                          <Icon name="agenda" />
                        </div>
                        {pilotCatalog ? (
                          <Engagements
                            catalog={pilotCatalog}
                            engagements={engagements}
                            upcoming
                            onEvent={(id) => {
                              setEventId(id);
                              navigate("agenda");
                            }}
                            onReservation={(id) => {
                              setReservationId(id);
                              navigate("reserve");
                            }}
                          />
                        ) : (
                          <p className="muted">
                            Vos inscriptions et réservations apparaîtront ici
                            quand ces services seront ouverts.
                          </p>
                        )}
                        <button
                          className="text-button"
                          onClick={() => navigate("guild", "requests")}
                        >
                          Retrouver mes demandes ({requests.length})
                          <Icon name="arrow" />
                        </button>
                      </section>
                      <div className="quick-grid">
                        <button
                          className="quick-action"
                          onClick={() => navigate("guild")}
                        >
                          <Icon name="guild" />
                          <strong>Exprimer un besoin</strong>
                          <span>
                            Le bon lien pour avancer
                            <Icon name="arrow" size={17} />
                          </span>
                        </button>
                        <button
                          className="quick-action blue"
                          onClick={() => navigate("profile")}
                        >
                          <Icon name="profile" />
                          <strong>
                            {session.profile?.offered_skills.length
                              ? "Mon profil dans la Guilde"
                              : "Ce que je peux apporter"}
                          </strong>
                          <span>
                            À votre façon, à votre rythme
                            <Icon name="arrow" size={17} />
                          </span>
                        </button>
                      </div>
                    </>
                  )}
                  <section>
                    <div className="section-heading">
                      <h2>Ouvrir des possibles</h2>
                      <button
                        className="text-button"
                        onClick={() => navigate("places")}
                      >
                        Les lieux
                        <Icon name="arrow" size={16} />
                      </button>
                    </div>
                    {catalog.places.slice(0, 2).map((place) => (
                      <button
                        key={place.id}
                        className="discovery-row"
                        onClick={() => {
                          setPlaceId(place.id);
                          navigate("agenda");
                        }}
                      >
                        <span className="discovery-icon">
                          <Icon name="places" />
                        </span>
                        <span>
                          <strong>{place.name}</strong>
                          <span>{place.description}</span>
                        </span>
                        <Icon name="arrow" />
                      </button>
                    ))}
                    {catalog.demo && (
                      <p className="fineprint">
                        Catalogue synthétique pour explorer les parcours.
                      </p>
                    )}
                  </section>
                  <div className="journal-end">
                    <Icon name="check" size={16} />
                    <span>Vous avez fait le tour du Lobby.</span>
                  </div>
                </>
              )}
              {route === "guild" && (
                <GuildScreen
                  key={session.person?.id ?? "visitor"}
                  tab={guildTab}
                  onTabChange={setGuildTab}
                  session={session}
                  catalog={catalog}
                  onProfile={() => navigate("profile")}
                  draft={draft}
                  onDraft={setDraft}
                  needs={needs}
                  requests={displayRequests}
                  availability={activeAvailability}
                  onAuth={() => setAuthOpen(true)}
                  onRefresh={refresh}
                  onAvailability={(available) =>
                    void mutate(
                      () => api.setAvailability(available),
                      available
                        ? "Vous êtes disponible pendant une heure. Vous pouvez vous retirer à tout moment."
                        : "Votre disponibilité est retirée.",
                    )
                  }
                  onRespond={respond}
                  onNotify={setNotice}
                  busy={busy}
                />
              )}
              {route === "agenda" &&
                (pilotCatalog ? (
                  <OperationalAgenda
                    key={privateStateVersion}
                    catalog={pilotCatalog}
                    engagements={engagements}
                    placeId={placeId}
                    onPlaceChange={setPlaceId}
                    eventId={eventId}
                    onEvent={setEventId}
                    signedIn={!!session.person}
                    onAuth={() => setAuthOpen(true)}
                    onRefresh={refresh}
                  />
                ) : (
                  <AgendaScreen
                    catalog={catalog}
                    placeId={placeId}
                    onPlaceChange={setPlaceId}
                    onPlace={(id) => {
                      navigate("places");
                      setSelectedPlace(id);
                    }}
                  />
                ))}
              {route === "places" && (
                <PlacesScreen
                  catalog={catalog}
                  selectedId={selectedPlace}
                  onSelect={setSelectedPlace}
                  onExplore={(id, target) => {
                    setPlaceId(id);
                    if (target === "guild")
                      setDraft((value) => ({
                        ...value,
                        place_id: id,
                        distance_preference: "onsite",
                      }));
                    navigate(target);
                  }}
                />
              )}
              {route === "reserve" &&
                (pilotCatalog ? (
                  <OperationalReserve
                    key={privateStateVersion}
                    catalog={pilotCatalog}
                    engagements={engagements}
                    placeId={placeId}
                    onPlaceChange={setPlaceId}
                    draft={reservationDraft}
                    onDraft={(value) => {
                      if (currentPerson.current === session.person?.id)
                        setReservationDraft(value);
                    }}
                    reservationId={reservationId}
                    onReservation={(value) => {
                      if (currentPerson.current === session.person?.id)
                        setReservationId(value);
                    }}
                    signedIn={!!session.person}
                    onAuth={() => setAuthOpen(true)}
                    onRefresh={refresh}
                  />
                ) : (
                  <ReserveScreen
                    key={privateStateVersion}
                    draft={reservationDraft}
                    onDraft={setReservationDraft}
                    catalog={catalog}
                    placeId={placeId}
                    onPlaceChange={setPlaceId}
                  />
                ))}
              {route === "card" && (
                <CardScreen
                  key={privateStateVersion}
                  personId={session.person?.id}
                  signedIn={!!session.person}
                  onAuth={() => setAuthOpen(true)}
                />
              )}
              {route === "profile" &&
                (session.profile ? (
                  <ProfileScreen
                    key={session.person?.id}
                    profile={session.profile}
                    catalog={catalog}
                    onSave={async () => {
                      applySession(await api.session());
                    }}
                    onNotification={async (notification) => {
                      const actor = currentPerson.current;
                      try {
                        if (
                          notification.kind.startsWith("event_") &&
                          pilotCatalog
                        ) {
                          const data = await pilotApi.engagements();
                          if (actor !== currentPerson.current) return;
                          setEngagements(data);
                          const registration = data.registrations.find(
                            (r) => r.id === notification.resource_id,
                          );
                          if (registration) {
                            setEventId(registration.event_id);
                            navigate("agenda");
                          }
                        } else if (
                          notification.kind.startsWith("space_reservation_") &&
                          pilotCatalog
                        ) {
                          await refresh();
                          if (actor !== currentPerson.current) return;
                          setReservationId(notification.resource_id);
                          navigate("reserve");
                        } else if (actor === currentPerson.current)
                          navigate("guild", "requests");
                      } catch (failure) {
                        if (actor === currentPerson.current)
                          setError(errorMessage(failure));
                      }
                    }}
                    onRequests={() => navigate("guild", "requests")}
                    onLogout={() => void logout()}
                  />
                ) : (
                  <>
                    <PageTitle
                      eyebrow="Votre place dans le réseau"
                      title="Mon profil"
                    >
                      Une présentation à votre image, visible selon vos choix.
                    </PageTitle>
                    <section className="panel">
                      <Empty
                        icon="profile"
                        title="Commençons par faire connaissance."
                      >
                        Le compte gratuit vous ouvre les mises en relation et
                        garde vos demandes au même endroit.
                      </Empty>
                      <button
                        className="button primary full"
                        onClick={() => setAuthOpen(true)}
                      >
                        Créer mon compte ou me connecter
                        <Icon name="arrow" />
                      </button>
                    </section>
                  </>
                ))}
              {route === "crm" && (
                <CrmScreen
                  key={privateStateVersion}
                  session={session}
                  onAuth={() => {
                    setAuthMode("login");
                    setAuthOpen(true);
                  }}
                />
              )}
              {route === "staff" && (
                <StaffScreen
                  key={privateStateVersion}
                  session={session}
                  onAuth={() => {
                    setAuthMode("login");
                    setAuthOpen(true);
                  }}
                />
              )}
            </>
          )}
          <footer className="page-footer">
            Avalon · Des lieux, des liens.
          </footer>
        </main>
      </div>
      {authOpen && (
        <AuthDialog
          initialMode={authMode}
          onClose={() => setAuthOpen(false)}
          onSuccess={(result) => {
            applySession(result);
            setAuthOpen(false);
            setError("");
            setNotice(
              "Vous êtes connecté. Retrouvez votre visibilité et vos compétences dans Profil.",
            );
          }}
        />
      )}
    </div>
  );
}

function StaffScreen({
  session,
  onAuth,
}: {
  session: Session;
  onAuth: () => void;
}) {
  const [requests, setRequests] = useState<Introduction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRequests((await api.staffRequests()).requests);
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (session.role === "staff") void load();
    else setLoading(false);
  }, [session.role, load]);
  return (
    <>
      <PageTitle
        eyebrow="Avalon OS · Intendance"
        title="Suivre les mises en relation"
      >
        Une vue opérationnelle limitée aux informations nécessaires au suivi.
      </PageTitle>
      {!session.person ? (
        <button className="button primary" onClick={onAuth}>
          Se connecter avec un compte habilité
        </button>
      ) : session.role !== "staff" ? (
        <Empty icon="shield" title="Un accès d’intendance est nécessaire.">
          Le rôle membre ne donne aucun droit d’administration.
        </Empty>
      ) : (
        <>
          <div className="section-heading">
            <Badge>{requests.length} demandes</Badge>
            <button
              className="text-button"
              disabled={loading}
              onClick={() => void load()}
            >
              <Icon name="refresh" />
              Actualiser
            </button>
          </div>
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
          {loading ? (
            <p className="muted" role="status">
              Chargement du suivi…
            </p>
          ) : !requests.length ? (
            <Empty title="Aucune demande à suivre." />
          ) : (
            <div className="stack">
              {requests.map((request) => (
                <article className="panel" key={request.id}>
                  <div className="section-heading">
                    <h2>
                      {request.sender_name} → {request.recipient_name}
                    </h2>
                    <Badge>
                      {
                        {
                          pending: "En attente",
                          accepted: "Acceptée",
                          declined: "Déclinée",
                          withdrawn: "Retirée",
                          expired: "Expirée",
                        }[request.status]
                      }
                    </Badge>
                  </div>
                  <p>{request.context}</p>
                  <dl className="reference-list">
                    <div>
                      <dt>Référence de demande</dt>
                      <dd>{request.id}</dd>
                    </div>
                    <div>
                      <dt>Identité commune / CRM</dt>
                      <dd>
                        {request.crm_person_id || request.sender_person_id}
                      </dd>
                    </div>
                    <div>
                      <dt>Expiration</dt>
                      <dd>{dateTime(request.expires_at)}</dd>
                    </div>
                  </dl>
                  {request.tree_references?.length ? (
                    <details>
                      <summary>Référence structurelle Tree</summary>
                      {request.tree_references.map((reference, index) => (
                        <div className="note block" key={index}>
                          <strong>
                            {reference.label ||
                              reference.node_id ||
                              reference.reference_id ||
                              reference.id ||
                              "Référence Tree"}
                          </strong>
                          <p>
                            {reference.synthetic
                              ? "Démonstration synthétique · "
                              : ""}
                            {reference.catalog_id} ·{" "}
                            {reference.provenance ||
                              "Provenance à consulter dans le système propriétaire."}
                          </p>
                          <p>{reference.justification}</p>
                          <p className="fineprint">
                            Cette référence ne prouve ni compétence ni
                            disponibilité d’une personne.
                          </p>
                        </div>
                      ))}
                    </details>
                  ) : (
                    <p className="fineprint">
                      Aucun rattachement structurel justifié.
                    </p>
                  )}
                </article>
              ))}
            </div>
          )}
          <p className="note">
            <Icon name="shield" />
            Cette vue ne donne accès à aucune note CRM privée. Une mise en
            relation n’est pas un consentement commercial.
          </p>
        </>
      )}
    </>
  );
}
